/**
 * MANUAL ORDER (KPR-1396; keepr-api utils/rankUtils.js, docs/SCHEMA.md
 * "Manual order"). A number element with `options.rank` holds the order
 * people drag items into; keepr writes it, nobody types it. A list SORTED by
 * one (the table, the board's columns) can be reordered: a drop sends the
 * item's new neighbours to `POST /api/items/{id}/rank`, and the server writes
 * a value between theirs.
 *
 * Pure: which element orders a list, and the neighbours a drop or a menu's
 * Move up / Move down / Move to top means, from the rows on screen.
 *
 * The API's words: `after` is the item it goes right AFTER in the order (a
 * lower rank), `before` the item it goes right BEFORE (a higher rank). A list
 * sorted descending shows the order upside down, so its row above is the
 * order's `before`.
 */
import { normalizeId } from './ids';

const RANK_TYPES = ['number', 'decimal'];

/** Is this element a manual order? */
export const isRankElement = def => !!def && def.options?.rank === true && RANK_TYPES.includes(def.dataType);

/**
 * The manual order a list is sorted by, or null: `{ element, direction }`.
 * `sortMeta` is the list's sort option (hooks/useListSort): an element sort
 * carries the element's dataType and options.
 */
export function rankSortOf({ sortField, sortDirection, sortMeta }) {
	if (!sortField || !sortMeta || sortMeta.value !== sortField) return null;
	if (!isRankElement({ dataType: sortMeta.dataType, options: sortMeta.options })) return null;
	return { element: sortField, direction: sortDirection === 'desc' ? 'desc' : 'asc' };
}

const rankOf = (item, element) => {
	const v = item?.elements?.[element];
	return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

/**
 * The rows sorted the way a list sorted by the rank shows them: ranked
 * ascending (or descending), blanks last, ties in the order given (the server
 * sent them in `_id` order). Used by the board, whose columns keep the order
 * the source read them in.
 */
export function sortByRank(items, { element, direction = 'asc' }) {
	const dir = direction === 'desc' ? -1 : 1;
	return (items || [])
		.map((item, index) => ({ item, index, value: rankOf(item, element) }))
		.sort((a, b) => {
			const an = a.value !== null;
			const bn = b.value !== null;
			if (an !== bn) return an ? -1 : 1;
			if (an && a.value !== b.value) return (a.value - b.value) * dir;
			return a.index - b.index;
		})
		.map(entry => entry.item);
}

/**
 * Where an item lands when it moves to `toIndex` among `rows` (the rows on
 * screen, in order, the moving one among them): `{ after, before }` item ids
 * for the API, or `null` when it would not move. `canNeighbour(row)` says
 * whether a row can be a neighbour (same collection, same order); rows that
 * cannot are skipped, so the nearest that can is sent instead.
 */
export function placementAt(rows, itemId, toIndex, { direction = 'asc', canNeighbour = () => true, element = null } = {}) {
	const id = normalizeId(itemId);
	const list = (rows || []).filter(Boolean);
	const from = list.findIndex(row => normalizeId(row?._id) === id);
	if (from < 0) return null;
	const target = Math.max(0, Math.min(list.length - 1, toIndex));
	if (target === from) return null;
	return placementInto(list.filter((_, i) => i !== from), target, { direction, canNeighbour, element });
}

/**
 * The placement of an item inserted at `index` among `rest` (the rows on
 * screen without it). Above: the nearest row before the place it lands that
 * can be a neighbour; below: the nearest after.
 */
function placementInto(rest, index, { direction = 'asc', canNeighbour = () => true, element = null } = {}) {
	// A list sorted DESCENDING shows the ranked rows upside down, but keeps
	// blanks last and ties in `_id` order in both directions, so a blank row,
	// or the lower of two tied rows, is not the order's neighbour there: only
	// ranked rows are sent, and of a tie only the row above.
	const desc = direction === 'desc';
	const usable = row => canNeighbour(row) && (!desc || !element || rankOf(row, element) !== null);
	const above = rest.slice(0, index).reverse().find(usable) || null;
	let below = rest.slice(index).find(usable) || null;
	if (desc && element && above && below && rankOf(above, element) === rankOf(below, element)) below = null;
	if (!above && !below) return null;
	const aboveId = above ? normalizeId(above._id) : null;
	const belowId = below ? normalizeId(below._id) : null;
	return desc
		? { after: belowId, before: aboveId }
		: { after: aboveId, before: belowId };
}

/**
 * A drop ON another row (a board tile): the item lands just before it
 * (`side: 'before'`, the row's upper half) or just after it ('after').
 * `rows` may hold the moving item or not (a card dragged in from another
 * column). → a placement, or null when it would not move.
 */
export function placementBeside(rows, itemId, anchorId, side = 'before', { direction = 'asc', canNeighbour = () => true, element = null } = {}) {
	const id = normalizeId(itemId);
	const anchor = normalizeId(anchorId);
	if (!anchor || anchor === id) return null;
	const list = (rows || []).filter(Boolean);
	const from = list.findIndex(row => normalizeId(row?._id) === id);
	const rest = list.filter(row => normalizeId(row?._id) !== id);
	const at = rest.findIndex(row => normalizeId(row?._id) === anchor);
	if (at < 0) return null;
	const index = side === 'after' ? at + 1 : at;
	// Already there: the same place it stands.
	if (from >= 0 && index === from) return null;
	return placementInto(rest, index, { direction, canNeighbour, element });
}

/**
 * Move up / Move down / Move to top from an item's menu. `step` is 'up',
 * 'down' or 'top'. → a placement (`{ after, before }` or `{ to }`), or null
 * when there is nowhere to go (the first row moving up).
 */
export function stepPlacement(rows, itemId, step, { direction = 'asc', canNeighbour = () => true, element = null } = {}) {
	if (step === 'top') return { to: direction === 'desc' ? 'bottom' : 'top' };
	const id = normalizeId(itemId);
	const list = (rows || []).filter(Boolean);
	const from = list.findIndex(row => normalizeId(row?._id) === id);
	if (from < 0) return null;
	if (step === 'up') {
		// Past the nearest row above that can be a neighbour.
		let i = from - 1;
		while (i >= 0 && !canNeighbour(list[i])) i -= 1;
		return i < 0 ? null : placementAt(list, id, i, { direction, canNeighbour, element });
	}
	if (step === 'down') {
		let i = from + 1;
		while (i < list.length && !canNeighbour(list[i])) i += 1;
		return i >= list.length ? null : placementAt(list, id, i, { direction, canNeighbour, element });
	}
	return null;
}

/**
 * The rank an item will hold once a placement lands, as near as the rows on
 * screen can tell: drawn while the request runs, replaced by the server's.
 */
export function estimatedRank(rows, placement, element) {
	if (!placement) return null;
	const byId = new Map((rows || []).map(row => [normalizeId(row?._id), row]));
	const values = (rows || []).map(row => rankOf(row, element)).filter(v => v !== null);
	if (placement.to === 'top') return values.length ? Math.min(...values) - 1 : 1;
	if (placement.to === 'bottom') return values.length ? Math.max(...values) + 1 : 1;
	const lo = placement.after ? rankOf(byId.get(placement.after), element) : null;
	const hi = placement.before ? rankOf(byId.get(placement.before), element) : null;
	if (lo !== null && hi !== null) return lo + (hi - lo) / 2;
	if (lo !== null) return lo + 0.5;
	if (hi !== null) return hi - 0.5;
	return null;
}
